import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {io} from 'socket.io-client';
import {getMe} from 'API/v1/GetMe';
import {playerHealth} from 'utils/playerHealth';
import {PLAYER_VARIANT_STORAGE_KEY, normalizePlayerVariant} from 'utils/playerVariant';
import {login as loginApi} from '../API/v1/Login';

export const AuthContext = createContext();
const ADMIN_ROLES = ['ROLE_ADMIN'];
const PROFILE_REFRESH_INTERVAL_MS = 60000;

export default function AuthContextProvider({children}) {
  const profileRequestRef = useRef(0);
  const latestProfilePromiseRef = useRef(null);
  const authEpochRef = useRef(0);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem(process.env.REACT_APP_TOKEN_NAME);
  });
  const [playerVariant, setPlayerVariant] = useState(() => {
    return normalizePlayerVariant(localStorage.getItem(PLAYER_VARIANT_STORAGE_KEY));
  });
  const [socket] = useState(() => {
    return io(process.env.REACT_APP_WEBSOCKET_URL);
  });

  const getPublicPathFromLocalStorage = useCallback(function () {
    const publicPath = localStorage.getItem(process.env.REACT_APP_PUBLIC_PATH_NAME);
    return publicPath ? publicPath : '';
  }, []);

  const persistProfile = useCallback((me) => {
    if (!me || !me.publicPath) {
      throw new Error('The server did not return a valid user profile.');
    }
    const variant = normalizePlayerVariant(me.playerVariant);
    localStorage.setItem(process.env.REACT_APP_PUBLIC_PATH_NAME, me.publicPath);
    localStorage.setItem(process.env.REACT_APP_ROLES_NAME, me.roles || []);
    localStorage.setItem(PLAYER_VARIANT_STORAGE_KEY, variant);
    setPlayerVariant(variant);
    return me;
  }, []);

  const refreshProfile = useCallback(
    (options = {}) => {
      const requestId = ++profileRequestRef.current;
      const authEpoch = authEpochRef.current;
      const token = localStorage.getItem(process.env.REACT_APP_TOKEN_NAME);
      if (!token) return Promise.resolve(null);

      let requestPromise;
      const followWinner = () => {
        if (authEpoch !== authEpochRef.current) return null;
        const winner = latestProfilePromiseRef.current;
        return winner && winner !== requestPromise ? winner : null;
      };
      requestPromise = Promise.resolve()
        .then(() => getMe(options))
        .then((me) => {
          // Overlapping focus/websocket/recovery refreshes remain latest-wins,
          // but every superseded caller follows the same winning promise. This
          // prevents a valid caller from interpreting supersession as unknown/C.
          if (authEpoch !== authEpochRef.current) return null;
          if (requestId !== profileRequestRef.current) return followWinner();
          if (!localStorage.getItem(process.env.REACT_APP_TOKEN_NAME) || !me) return null;
          return persistProfile(me);
        })
        .catch(() => {
          if (authEpoch !== authEpochRef.current) return null;
          if (requestId !== profileRequestRef.current) return followWinner();
          // Keep the last known assignment during transient profile failures.
          return null;
        });
      latestProfilePromiseRef.current = requestPromise;
      return requestPromise;
    },
    [persistProfile]
  );

  const login = useCallback(
    async function (username, password) {
      const authEpoch = ++authEpochRef.current;
      const requestId = ++profileRequestRef.current;
      latestProfilePromiseRef.current = null;
      playerHealth.reset();
      try {
        const data = await loginApi(username, password);
        if (requestId !== profileRequestRef.current || authEpoch !== authEpochRef.current) return;
        localStorage.setItem(process.env.REACT_APP_TOKEN_NAME, data.token);
        localStorage.setItem(process.env.REACT_APP_REFRESH_TOKEN_NAME, data.refresh_token);
        const me = await getMe();
        if (requestId !== profileRequestRef.current || authEpoch !== authEpochRef.current) {
          return;
        }
        persistProfile(me);
        setIsAuthenticated(true);
      } catch (error) {
        if (requestId !== profileRequestRef.current || authEpoch !== authEpochRef.current) return;
        localStorage.removeItem(process.env.REACT_APP_TOKEN_NAME);
        localStorage.removeItem(process.env.REACT_APP_REFRESH_TOKEN_NAME);
        localStorage.removeItem(process.env.REACT_APP_PUBLIC_PATH_NAME);
        localStorage.removeItem(process.env.REACT_APP_ROLES_NAME);
        localStorage.removeItem(PLAYER_VARIANT_STORAGE_KEY);
        setPlayerVariant('A');
        setIsAuthenticated(false);
      }
    },
    [persistProfile]
  );

  const logout = useCallback(function () {
    authEpochRef.current += 1;
    profileRequestRef.current += 1;
    latestProfilePromiseRef.current = null;
    playerHealth.reset();
    localStorage.removeItem(process.env.REACT_APP_TOKEN_NAME);
    localStorage.removeItem(process.env.REACT_APP_REFRESH_TOKEN_NAME);
    localStorage.removeItem(process.env.REACT_APP_PUBLIC_PATH_NAME);
    localStorage.removeItem(process.env.REACT_APP_ROLES_NAME);
    localStorage.removeItem(PLAYER_VARIANT_STORAGE_KEY);
    setPlayerVariant('A');
    setIsAuthenticated(false);
  }, []);

  // Refresh the server-assigned playback variant for restored sessions and
  // whenever the user returns to the app. This lets an admin enable Player C
  // without requiring the user to log out and back in.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    refreshProfile();
    const handleFocus = () => refreshProfile();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshProfile();
    };
    const refreshTimer = window.setInterval(refreshProfile, PROFILE_REFRESH_INTERVAL_MS);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(refreshTimer);
    };
  }, [isAuthenticated, refreshProfile]);

  // Socket.IO may reconnect with a new socket id after a network cut. Register
  // the authenticated publicPath on every connect as well as immediately for
  // an already-connected socket; websocket delivery must never depend on the
  // first connection surviving for the whole browser session.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const registerFrontend = () => {
      const publicPath = getPublicPathFromLocalStorage();
      if (publicPath) socket.emit('frontend', {login: publicPath});
    };
    registerFrontend();
    socket.on('connect', registerFrontend);
    return () => socket.off('connect', registerFrontend);
  }, [isAuthenticated, playerVariant, socket, getPublicPathFromLocalStorage]);

  const isAdmin = useCallback(function () {
    const roles = localStorage.getItem(process.env.REACT_APP_ROLES_NAME);
    if (roles) {
      return ADMIN_ROLES.some((role) => roles.includes(role));
    }
    return false;
  }, []);

  const value = useMemo(
    () => ({
      login,
      logout,
      isAuthenticated,
      socket,
      isAdmin,
      getPublicPathFromLocalStorage,
      playerVariant,
      refreshProfile
    }),
    [
      isAuthenticated,
      login,
      logout,
      socket,
      isAdmin,
      getPublicPathFromLocalStorage,
      playerVariant,
      refreshProfile
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthContextProvider.propTypes = {
  children: PropTypes.node
};

export function useAuthContext() {
  return useContext(AuthContext);
}
